import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "config";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  Heading,
  Text,
  keyframes,
  useToast,
  Icon,
  Switch,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { FaGlobe, FaCoins, FaLightbulb, FaWallet, FaArrowRight, FaArrowLeft, FaCheck, FaInfoCircle } from "react-icons/fa";
import MufynButton from "components/Button";

// ============================================
// ANIMATIONS
// ============================================
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const slideInRight = keyframes`
  0% { opacity: 0; transform: translateX(60px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const slideInLeft = keyframes`
  0% { opacity: 0; transform: translateX(-60px); }
  100% { opacity: 1; transform: translateX(0); }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// ============================================
// CONSTANTS
// ============================================

const LANGUAGES = [
  { code: "en", label: "English", flagCode: "gb" },
  { code: "fr", label: "Français", flagCode: "fr" },
  { code: "de", label: "Deutsch", flagCode: "de" },
  { code: "es", label: "Español", flagCode: "es" },
  { code: "it", label: "Italiano", flagCode: "it" },
];

const CURRENCIES = [
  { code: "CHF", symbol: "CHF", label: "Swiss Franc", flagCode: "ch" },
  { code: "€", symbol: "€", label: "Euro", flagCode: "eu" },
  { code: "$", symbol: "$", label: "US Dollar", flagCode: "us" },
];

const TOTAL_STEPS = 4;

// ============================================
// HELPERS
// ============================================

/** Detect best default language from browser */
function detectLanguage() {
  const browserLang = (navigator.language || navigator.userLanguage || "en").split("-")[0].toLowerCase();
  const match = LANGUAGES.find((l) => l.code === browserLang);
  return match ? match.code : "en";
}

/** Detect best default currency from browser locale */
function detectCurrency() {
  const locale = navigator.language || navigator.userLanguage || "";
  const parts = locale.split("-");
  const region = parts.length > 1 ? parts[parts.length - 1].toUpperCase() : null;
  const lang = parts[0].toLowerCase();

  if (region === "CH") return "CHF";
  if (lang === "en") return "$";
  return "€";
}

// ============================================
// SHARED STYLES
// ============================================

const bgStyle = {
  minH: "100vh",
  w: "100%",
  position: "relative",
  overflow: "hidden",
  bg: "#0a0d14",
};

const bgOverlayStyle = {
  position: "absolute",
  top: "0",
  left: "0",
  right: "0",
  bottom: "0",
  zIndex: "0",
};

const bgGradientStyle = {
  position: "absolute",
  top: "0",
  left: "0",
  right: "0",
  bottom: "0",
  bgGradient:
    "radial(ellipse at 50% 30%, rgba(34, 197, 94, 0.12) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)",
};

// ============================================
// SELECTION CARD COMPONENT
// ============================================

function FlagImage({ code, size = 28 }) {
  const w = size;
  const h = Math.round(size * 0.75);
  return (
    <img
      src={`https://flagcdn.com/${w * 2}x${h * 2}/${code}.png`}
      srcSet={`https://flagcdn.com/${w * 3}x${h * 3}/${code}.png 2x`}
      width={w}
      height={h}
      alt={code}
      style={{ display: "inline-block", borderRadius: "4px", flexShrink: 0 }}
    />
  );
}

function SelectionCard({ selected, onClick, icon, label, sublabel, flagCode }) {
  return (
    <Flex
      as="button"
      onClick={onClick}
      align="center"
      gap={4}
      p={4}
      w="100%"
      borderRadius="16px"
      border="1px solid"
      borderColor={selected ? "rgba(34, 197, 94, 0.6)" : "rgba(255, 255, 255, 0.08)"}
      bg={selected ? "rgba(34, 197, 94, 0.12)" : "rgba(15, 20, 30, 0.5)"}
      boxShadow={selected ? "0 0 20px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)" : "none"}
      cursor="pointer"
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        bg: selected ? "rgba(34, 197, 94, 0.15)" : "rgba(15, 20, 30, 0.8)",
        borderColor: selected ? "rgba(34, 197, 94, 0.7)" : "rgba(255, 255, 255, 0.15)",
        transform: "translateY(-1px)",
      }}
      _active={{
        transform: "scale(0.98)",
      }}
      role="button"
      tabIndex={0}
    >
      {/* Selection indicator */}
      <Flex
        align="center"
        justify="center"
        w="20px"
        h="20px"
        borderRadius="full"
        border="2px solid"
        borderColor={selected ? "#22c55e" : "rgba(255,255,255,0.2)"}
        bg={selected ? "#22c55e" : "transparent"}
        flexShrink={0}
        transition="all 0.3s ease"
      >
        {selected && <Icon as={FaCheck} color="white" boxSize="10px" />}
      </Flex>

      {/* Flag / Icon */}
      {flagCode ? (
        <FlagImage code={flagCode} size={28} />
      ) : icon ? (
        <Icon as={icon} color={selected ? "#4ade80" : "gray.400"} boxSize="22px" flexShrink={0} />
      ) : null}

      {/* Labels */}
      <Flex direction="column" align="flex-start" flex={1}>
        <Text color="white" fontSize="md" fontWeight="600" lineHeight="1.3">
          {label}
        </Text>
        {sublabel && (
          <Text color="gray.500" fontSize="sm" lineHeight="1.3">
            {sublabel}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

// ============================================
// STEP COMPONENTS
// ============================================

function StepLanguage({ value, onChange, direction, t }) {
  const animKey = `lang-${direction}`;
  return (
    <Box
      key={animKey}
      animation={`${direction === "forward" ? slideInRight : slideInLeft} 0.45s cubic-bezier(0.4, 0, 0.2, 1)`}
    >
      <Flex direction="column" align="center" mb={8}>
        <Flex
          align="center"
          justify="center"
          w="64px"
          h="64px"
          borderRadius="20px"
          bg="rgba(34, 197, 94, 0.1)"
          border="1px solid rgba(34, 197, 94, 0.2)"
          mb={5}
          animation={`${float} 3s ease-in-out infinite`}
        >
          <Icon as={FaGlobe} color="#4ade80" boxSize="28px" />
        </Flex>
        <Heading
          color="white"
          fontSize={{ base: "24px", md: "28px" }}
          fontWeight="bold"
          textAlign="center"
          mb={2}
        >
          {t("onboarding.language.title")}
        </Heading>
        <Text color="gray.400" fontSize="md" textAlign="center" maxW="340px">
          {t("onboarding.language.subtitle")}
        </Text>
      </Flex>

      <Flex direction="column" gap={3} maxW="400px" mx="auto">
        {LANGUAGES.map((lang) => (
          <SelectionCard
            key={lang.code}
            selected={value === lang.code}
            onClick={() => onChange(lang.code)}
            flagCode={lang.flagCode}
            label={lang.label}
          />
        ))}
      </Flex>
    </Box>
  );
}

function StepCurrency({ value, onChange, direction, t }) {
  const animKey = `currency-${direction}`;
  return (
    <Box
      key={animKey}
      animation={`${direction === "forward" ? slideInRight : slideInLeft} 0.45s cubic-bezier(0.4, 0, 0.2, 1)`}
    >
      <Flex direction="column" align="center" mb={8}>
        <Flex
          align="center"
          justify="center"
          w="64px"
          h="64px"
          borderRadius="20px"
          bg="rgba(34, 197, 94, 0.1)"
          border="1px solid rgba(34, 197, 94, 0.2)"
          mb={5}
          animation={`${float} 3s ease-in-out infinite`}
        >
          <Icon as={FaCoins} color="#4ade80" boxSize="28px" />
        </Flex>
        <Heading
          color="white"
          fontSize={{ base: "24px", md: "28px" }}
          fontWeight="bold"
          textAlign="center"
          mb={2}
        >
          {t("onboarding.currency.title")}
        </Heading>
        <Text color="gray.400" fontSize="md" textAlign="center" maxW="380px">
          {t("onboarding.currency.subtitle")}
        </Text>
      </Flex>

      <Flex direction="column" gap={3} maxW="400px" mx="auto">
        {CURRENCIES.map((cur) => (
          <SelectionCard
            key={cur.code}
            selected={value === cur.code}
            onClick={() => onChange(cur.code)}
            flagCode={cur.flagCode}
            label={`${cur.symbol} — ${cur.label}`}
          />
        ))}
      </Flex>
    </Box>
  );
}

function StepTips({ value, onChange, direction, t }) {
  const animKey = `tips-${direction}`;
  return (
    <Box
      key={animKey}
      animation={`${direction === "forward" ? slideInRight : slideInLeft} 0.45s cubic-bezier(0.4, 0, 0.2, 1)`}
    >
      <Flex direction="column" align="center" mb={8}>
        <Flex
          align="center"
          justify="center"
          w="64px"
          h="64px"
          borderRadius="20px"
          bg="rgba(34, 197, 94, 0.1)"
          border="1px solid rgba(34, 197, 94, 0.2)"
          mb={5}
          animation={`${float} 3s ease-in-out infinite`}
        >
          <Icon as={FaLightbulb} color="#4ade80" boxSize="28px" />
        </Flex>
        <Heading
          color="white"
          fontSize={{ base: "24px", md: "28px" }}
          fontWeight="bold"
          textAlign="center"
          mb={2}
        >
          {t("onboarding.tips.title")}
        </Heading>
        <Text color="gray.400" fontSize="md" textAlign="center" maxW="380px">
          {t("onboarding.tips.subtitle")}
        </Text>
      </Flex>

      <Flex direction="column" align="center" maxW="400px" mx="auto" gap={6}>
        {/* Tips toggle */}
        <Box
          w="100%"
          p={6}
          borderRadius="20px"
          bg="rgba(15, 20, 30, 0.6)"
          border="1px solid"
          borderColor={value ? "rgba(34, 197, 94, 0.3)" : "rgba(255, 255, 255, 0.08)"}
          transition="all 0.3s ease"
        >
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel
                htmlFor="tips-toggle"
                mb={1}
                color="white"
                fontSize="md"
                fontWeight="600"
                cursor="pointer"
              >
                {t("onboarding.tips.toggle_label")}
              </FormLabel>
              <Text color="gray.500" fontSize="sm">
                {t("onboarding.tips.toggle_description")}
              </Text>
            </Box>
            <Switch
              id="tips-toggle"
              isChecked={value}
              onChange={(e) => onChange(e.target.checked)}
              size="lg"
              colorScheme="green"
            />
          </FormControl>
        </Box>

        {/* Preview info box */}
        <Box
          w="100%"
          p={5}
          borderRadius="16px"
          bg="rgba(34, 197, 94, 0.06)"
          border="1px solid rgba(34, 197, 94, 0.15)"
        >
          <Flex align="flex-start" gap={3}>
            <Icon as={FaLightbulb} color="#4ade80" boxSize="16px" mt="3px" flexShrink={0} />
            <Text color="gray.300" fontSize="sm" lineHeight="1.6">
              {t("onboarding.tips.example_hint")}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

// ============================================
// STEP: INITIAL BALANCE
// ============================================

function getTodayDateString() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function StepInitialBalance({ amount, onAmountChange, date, onDateChange, currency, direction, t }) {
  const animKey = `balance-${direction}`;
  const currencySymbol = currency || "€";
  const currencyWidth = currencySymbol.length > 2 ? "4.5rem" : currencySymbol.length > 1 ? "3.5rem" : "3rem";
  return (
    <Box
      key={animKey}
      animation={`${direction === "forward" ? slideInRight : slideInLeft} 0.45s cubic-bezier(0.4, 0, 0.2, 1)`}
    >
      <Flex direction="column" align="center" mb={8}>
        <Flex
          align="center"
          justify="center"
          w="64px"
          h="64px"
          borderRadius="20px"
          bg="rgba(34, 197, 94, 0.1)"
          border="1px solid rgba(34, 197, 94, 0.2)"
          mb={5}
          animation={`${float} 3s ease-in-out infinite`}
        >
          <Icon as={FaWallet} color="#4ade80" boxSize="28px" />
        </Flex>
        <Heading
          color="white"
          fontSize={{ base: "24px", md: "28px" }}
          fontWeight="bold"
          textAlign="center"
          mb={2}
        >
          {t("onboarding.initial_balance.title")}
        </Heading>
        <Text color="gray.400" fontSize="md" textAlign="center" maxW="380px">
          {t("onboarding.initial_balance.subtitle")}
        </Text>
      </Flex>

      <Flex direction="column" align="center" maxW="400px" mx="auto" gap={5}>
        {/* Amount input */}
        <Box w="100%">
          <Flex justify="space-between" align="center" mb={2}>
            <Text color="gray.400" fontSize="sm" fontWeight="500">
              {t("onboarding.initial_balance.amount_label")}
            </Text>
            <Text color="gray.500" fontSize="xs" fontStyle="italic">
              {t("onboarding.initial_balance.optional")}
            </Text>
          </Flex>
          <InputGroup size="lg">
            <InputLeftElement
              pointerEvents="none"
              color="gray.500"
              fontSize="lg"
              fontWeight="600"
              justifyContent="start"
              pl="1rem"
              w={currencyWidth}
              children={currencySymbol}
            />
            <Input
              type="number"
              value={amount}
              onChange={(e) => onAmountChange(e.target.value)}
              placeholder="0.00"
              pl={currencyWidth}
              bg="rgba(15, 20, 30, 0.6)"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.08)"
              borderRadius="16px"
              color="white"
              fontSize="lg"
              _hover={{ borderColor: "rgba(255, 255, 255, 0.15)" }}
              _focus={{ borderColor: "rgba(34, 197, 94, 0.6)", boxShadow: "0 0 0 1px rgba(34, 197, 94, 0.3)" }}
              _placeholder={{ color: "gray.600" }}
              step="0.01"
              min="0"
            />
          </InputGroup>
        </Box>

        {/* Date input */}
        <Box w="100%">
          <Text color="gray.400" fontSize="sm" mb={2} fontWeight="500">
            {t("onboarding.initial_balance.date_label")}
          </Text>
          <Input
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            size="lg"
            bg="rgba(15, 20, 30, 0.6)"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.08)"
            borderRadius="16px"
            color="white"
            fontSize="md"
            _hover={{ borderColor: "rgba(255, 255, 255, 0.15)" }}
            _focus={{ borderColor: "rgba(34, 197, 94, 0.6)", boxShadow: "0 0 0 1px rgba(34, 197, 94, 0.3)" }}
            sx={{ colorScheme: "dark" }}
          />
        </Box>

        {/* Info box */}
        <Box
          w="100%"
          p={5}
          borderRadius="16px"
          bg="rgba(34, 197, 94, 0.06)"
          border="1px solid rgba(34, 197, 94, 0.15)"
        >
          <Flex align="flex-start" gap={3}>
            <Icon as={FaInfoCircle} color="#4ade80" boxSize="16px" mt="3px" flexShrink={0} />
            <Text color="gray.300" fontSize="sm" lineHeight="1.6">
              {t("onboarding.initial_balance.info_hint")}
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
}

// ============================================
// MAIN ONBOARDING COMPONENT
// ============================================

function Onboarding() {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const [step, setStep] = useState(0); // 0-indexed
  const [direction, setDirection] = useState("forward");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Defaults pre-detected from browser
  const [language, setLanguage] = useState(detectLanguage);
  const [currency, setCurrency] = useState(detectCurrency);
  const [tipsEnabled, setTipsEnabled] = useState(true);
  const [initialBalance, setInitialBalance] = useState("");
  const [initialBalanceDate, setInitialBalanceDate] = useState(getTodayDateString);

  // Apply language change immediately when user picks one
  useEffect(() => {
    if (language && language !== i18n.language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const goNext = () => {
    if (step < TOTAL_STEPS - 1) {
      setDirection("forward");
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setDirection("backward");
      setStep((s) => s - 1);
    }
  };

  const handleFinish = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        language,
        currency,
        tips_enabled: tipsEnabled ? "1" : "0",
      };

      const parsedBalance = parseFloat(initialBalance);
      if (!isNaN(parsedBalance) && parsedBalance > 0) {
        payload.initial_balance = parsedBalance;
        payload.initial_balance_date = initialBalanceDate || getTodayDateString();
      }

      await axios.post(`${config.apiUrl}/onboarding`, payload);

      // Redirect to dashboard or stored redirect
      const storedRedirect = (() => {
        try {
          return sessionStorage.getItem("mufyn_post_login_redirect")
            || localStorage.getItem("mufyn_post_login_redirect")
            || null;
        } catch { return null; }
      })();

      // Clean up stored redirect
      try {
        sessionStorage.removeItem("mufyn_post_login_redirect");
        localStorage.removeItem("mufyn_post_login_redirect");
      } catch (e) {}

      setTimeout(() => {
        window.location.href = storedRedirect ? `/#${storedRedirect}` : "/";
      }, 300);
    } catch (error) {
      toast({
        title: t("onboarding.error.title"),
        description: t("onboarding.error.description"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <Box {...bgStyle}>
      {/* Background */}
      <Box {...bgOverlayStyle}>
        <Box {...bgGradientStyle} />
      </Box>

      {/* Content */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        direction="column"
        align="center"
        justify="center"
        px={4}
        pt={8}
        pb={{ base: 20, md: 8 }}
      >
        {/* Progress indicator */}
        <Flex
          gap={2}
          mb={8}
          animation={`${fadeIn} 0.6s ease-out 0.2s both`}
        >
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <Box
              key={i}
              w={step === i ? "32px" : "8px"}
              h="8px"
              borderRadius="full"
              bg={step === i ? "#22c55e" : i < step ? "rgba(34, 197, 94, 0.4)" : "rgba(255, 255, 255, 0.1)"}
              transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
            />
          ))}
        </Flex>

        {/* Step content */}
        <Box w="100%" maxW="460px" minH="380px">
          {step === 0 && (
            <StepLanguage
              value={language}
              onChange={setLanguage}
              direction={direction}
              t={t}
            />
          )}
          {step === 1 && (
            <StepCurrency
              value={currency}
              onChange={setCurrency}
              direction={direction}
              t={t}
            />
          )}
          {step === 2 && (
            <StepTips
              value={tipsEnabled}
              onChange={setTipsEnabled}
              direction={direction}
              t={t}
            />
          )}
          {step === 3 && (
            <StepInitialBalance
              amount={initialBalance}
              onAmountChange={setInitialBalance}
              date={initialBalanceDate}
              onDateChange={setInitialBalanceDate}
              currency={currency}
              direction={direction}
              t={t}
            />
          )}
        </Box>

        {/* Navigation buttons */}
        <Flex
          mt={8}
          gap={4}
          w="100%"
          maxW="460px"
          justify="space-between"
          animation={`${fadeIn} 0.6s ease-out 0.3s both`}
        >
          {step > 0 ? (
            <MufynButton
              variant="ghost"
              onClick={goBack}
              leftIcon={<Icon as={FaArrowLeft} />}
              size="lg"
            >
              {t("onboarding.back")}
            </MufynButton>
          ) : (
            <Box />
          )}

          {isLastStep ? (
            <MufynButton
              variant="primary"
              onClick={handleFinish}
              isLoading={isSubmitting}
              rightIcon={<Icon as={FaCheck} />}
              size="lg"
              px={8}
            >
              {t("onboarding.finish")}
            </MufynButton>
          ) : (
            <MufynButton
              variant="primary"
              onClick={goNext}
              rightIcon={<Icon as={FaArrowRight} />}
              size="lg"
              px={8}
            >
              {t("onboarding.next")}
            </MufynButton>
          )}
        </Flex>

        {/* Hint that settings can be changed later */}
        <Text
          color="gray.600"
          fontSize="xs"
          mt={6}
          textAlign="center"
          maxW="360px"
          animation={`${fadeIn} 0.6s ease-out 0.4s both`}
        >
          {t("onboarding.settings_hint")}
        </Text>
      </Flex>
    </Box>
  );
}

export default Onboarding;
